import { apiFetch } from './http';

/** Envelope of every collection endpoint (07 §1.7). */
export interface PageInfo {
  limit: number;
  nextCursor: string | null;
  previousCursor: string | null;
  hasMore: boolean;
  totalCount: number | null;
  countExceededLimit: boolean;
}

export interface PageResponse<T> {
  items: T[];
  page: PageInfo;
}

export interface ListParams {
  limit?: number;
  cursor?: string;
  /** `name` sorts ascending, `-name` descending; only whitelisted fields (07 §1.8). */
  sort?: string;
  /** `field` or `field[op]` → value, exactly as the endpoint's whitelist admits them. */
  filters?: Record<string, string | undefined>;
  countExact?: boolean;
}

/** Builds the query string of a collection request; undefined filters are simply omitted. */
export function collectionQuery(params: ListParams): string {
  const query = new URLSearchParams();
  if (params.limit !== undefined) {
    query.set('limit', String(params.limit));
  }
  if (params.cursor !== undefined) {
    query.set('cursor', params.cursor);
  }
  if (params.sort !== undefined) {
    query.set('sort', params.sort);
  }
  if (params.countExact === true) {
    query.set('count', 'exact');
  }
  for (const [field, value] of Object.entries(params.filters ?? {})) {
    if (value !== undefined && value !== '') {
      query.set(field, value);
    }
  }
  const text = query.toString();
  return text.length === 0 ? '' : `?${text}`;
}

export function fetchPage<T>(
  path: string,
  params: ListParams,
  signal?: AbortSignal,
): Promise<PageResponse<T>> {
  return apiFetch<PageResponse<T>>(
    `${path}${collectionQuery(params)}`,
    signal === undefined ? {} : { signal },
  );
}

/** A record every catalog row carries: the id for routing and the ETag for `If-Match` (07 §1.10). */
export interface CatalogRow {
  id: string;
  eTag: string;
  /** Absent in the catalogs that have no activity flag (holidays, 05b §9.3). */
  isActive?: boolean | null;
  updatedAt: string;
}

/** Sends a merge-patch (07 §1.3) guarded by the ETag the row was read with. */
export function patchResource<T>(
  path: string,
  body: Record<string, unknown>,
  etag: string,
): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PATCH',
    body,
    headers: { 'Content-Type': 'application/merge-patch+json', 'If-Match': etag },
  });
}

export function deleteResource(path: string): Promise<void> {
  return apiFetch<void>(path, { method: 'DELETE' });
}

/**
 * Every item of a small catalog (the options of a select): follows the cursor page by page, up
 * to `maxPages`, so a tenant with more organizations than one page still sees them all.
 */
export async function fetchAllPages<T>(
  list: (params: ListParams, signal?: AbortSignal) => Promise<PageResponse<T>>,
  params: ListParams,
  signal?: AbortSignal,
  maxPages = 10,
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < maxPages; page += 1) {
    const response = await list({ ...params, ...(cursor === undefined ? {} : { cursor }) }, signal);
    items.push(...response.items);
    // `nextCursor: null` on the last page (07 §1.15); `?? null` also covers a mock omitting it.
    const next = response.page.nextCursor ?? null;
    if (next === null) {
      break;
    }
    cursor = next;
  }
  return items;
}

/** The four operations every catalog of 07 §4 exposes, over one collection path. */
export interface CatalogApi<T extends CatalogRow> {
  list: (params: ListParams, signal?: AbortSignal) => Promise<PageResponse<T>>;
  create: (body: Record<string, unknown>) => Promise<T>;
  update: (id: string, body: Record<string, unknown>, etag: string) => Promise<T>;
  remove: (id: string) => Promise<void>;
}

export function catalogApi<T extends CatalogRow>(path: string): CatalogApi<T> {
  return {
    list: (params, signal) => fetchPage<T>(path, params, signal),
    create: (body) => apiFetch<T>(path, { method: 'POST', body }),
    update: (id, body, etag) => patchResource<T>(`${path}/${id}`, body, etag),
    remove: (id) => deleteResource(`${path}/${id}`),
  };
}
